import { StyleSheet } from "@react-pdf/renderer";

export const PDF_COLORS = {
  border: "#e6e6e6",
  headerBg: "#f2f2f2",
  text: "#5f5f5f",
  textStrong: "#1a1a1a",
  white: "#ffffff",
  passBg: "#f4f4f5",
  passBorder: "#d4d4d8",
  passText: "#52525b",
  considerBg: "#fff7ed",
  considerBorder: "#fdba74",
  considerText: "#c2410c",
  recommendBg: "#ecfdf5",
  recommendBorder: "#6ee7b7",
  recommendText: "#047857",
  poorBg: "#fff1f2",
  poorText: "#be123c",
  fairBg: "#fff7ed",
  fairText: "#c2410c",
  goodBg: "#f0f9ff",
  goodText: "#0369a1",
  excellentBg: "#ecfdf5",
  excellentText: "#047857",
  idleText: "#a1a1aa",
} as const;

export const RECOMMENDATION_CHIP_COLORS: Record<
  string,
  { backgroundColor: string; borderColor: string; color: string }
> = {
  pass: {
    backgroundColor: PDF_COLORS.passBg,
    borderColor: PDF_COLORS.passBorder,
    color: PDF_COLORS.passText,
  },
  consider: {
    backgroundColor: PDF_COLORS.considerBg,
    borderColor: PDF_COLORS.considerBorder,
    color: PDF_COLORS.considerText,
  },
  recommend: {
    backgroundColor: PDF_COLORS.recommendBg,
    borderColor: PDF_COLORS.recommendBorder,
    color: PDF_COLORS.recommendText,
  },
};

export const RATING_CELL_COLORS: Record<number, { backgroundColor: string; color: string }> = {
  1: { backgroundColor: PDF_COLORS.poorBg, color: PDF_COLORS.poorText },
  2: { backgroundColor: PDF_COLORS.fairBg, color: PDF_COLORS.fairText },
  3: { backgroundColor: PDF_COLORS.goodBg, color: PDF_COLORS.goodText },
  4: { backgroundColor: PDF_COLORS.excellentBg, color: PDF_COLORS.excellentText },
};

export const styles = StyleSheet.create({
  topMargin: {
    marginTop: 20,
  },
  sectionMargin: {
    marginTop: 16,
  },
  heroBlock: {
    display: "flex",
    flexDirection: "column",
    marginTop: 12,
    padding: 12,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: PDF_COLORS.border,
    backgroundColor: PDF_COLORS.white,
  },
  titleText: {
    fontSize: 16,
    fontWeight: "bold",
    color: PDF_COLORS.textStrong,
  },
  loglineText: {
    fontSize: 11,
    color: PDF_COLORS.text,
    marginTop: 4,
  },
  metaLine: {
    fontSize: 10,
    color: PDF_COLORS.text,
    marginTop: 6,
  },
  heroFooter: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 10,
  },
  verdictColumn: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  scoreColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  scoreValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: PDF_COLORS.textStrong,
  },
  scoreLabel: {
    fontSize: 9,
    color: PDF_COLORS.text,
    textTransform: "uppercase",
    marginTop: 2,
  },
  verdictRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  verdictLabel: {
    fontSize: 9,
    color: PDF_COLORS.text,
    width: 42,
  },
  chip: {
    borderStyle: "solid",
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  chipText: {
    fontSize: 9,
    fontWeight: "bold",
  },
  sectionHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: PDF_COLORS.headerBg,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: PDF_COLORS.border,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  sectionHeaderText: {
    fontSize: 12,
    fontWeight: "bold",
    color: PDF_COLORS.textStrong,
  },
  sectionHeaderMeta: {
    fontSize: 10,
    color: PDF_COLORS.text,
  },
  fullWidth: {
    width: "100%",
  },
  tbody: {
    display: "flex",
    flexDirection: "row",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: PDF_COLORS.border,
    backgroundColor: PDF_COLORS.white,
  },
  thead: {
    backgroundColor: PDF_COLORS.headerBg,
    padding: 8,
  },
  theadText: {
    fontSize: 11,
    fontWeight: "medium",
    flexWrap: "nowrap",
  },
  text: {
    fontSize: 11,
    color: PDF_COLORS.text,
  },
  textPadding: {
    padding: 8,
  },
  detailContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    flexGrow: 1,
    flexShrink: 0,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: PDF_COLORS.border,
    backgroundColor: PDF_COLORS.white,
  },
  coverageRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
  },
  rubricRow: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: PDF_COLORS.border,
    backgroundColor: PDF_COLORS.white,
  },
  rubricLabel: {
    width: "28%",
    padding: 8,
    borderStyle: "solid",
    borderRightWidth: 1,
    borderColor: PDF_COLORS.border,
    backgroundColor: PDF_COLORS.headerBg,
    fontSize: 10,
    fontWeight: "medium",
    color: PDF_COLORS.textStrong,
  },
  ratingCell: {
    width: "18%",
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderStyle: "solid",
    borderRightWidth: 1,
    borderColor: PDF_COLORS.border,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: PDF_COLORS.white,
  },
  ratingCellLast: {
    borderRightWidth: 0,
  },
  ratingCellText: {
    fontSize: 9,
    textAlign: "center",
    color: PDF_COLORS.idleText,
  },
});
